import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { OpenSquashService } from '../shared/services/open-squash-service';
import { TournamentCategory } from '../shared/tournament-category';
import { CategoryGroup } from '../shared/category-group';
import { CommonModule } from '@angular/common';
import { PlayerGroup } from '../shared/player-group';
import { Observable, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CreatePlayerComponent } from '../admin/create-player-component/create-player-component';
import { Player } from '../shared/model/player';
import { AddPlayerToGroupComponent } from '../admin/add-player-to-group-component/add-player-to-group-component';
import { Match, MatchByDay, MatchExtended, MatchState } from '../shared/model/match';
import { Timestamp } from 'firebase/firestore';
import { CreateMatchComponent } from '../admin/create-match-component/create-match-component';
import { SetMatchScoreComponent } from '../admin/set-match-score-component/set-match-score-component';
import { ActivatedRoute } from '@angular/router';
import { CategoryFinalComponent } from '../category-final-component/category-final-component';
import { StartMatchComponent } from '../admin/start-match-component/start-match-component';
import { CategoryClassificationGroup } from '../category-classification-group/category-classification-group';
import { CreateMatchesComponent } from '../create-matches-component/create-matches-component';

export interface PlayerGroupExtended extends PlayerGroup {
  player?: Player;
  // order?: number;
  // matchesWon?: number;
  // matchesLost?: number;
  // setsWon?: number;
  // setsLost?: number;
  // pointsWon?: number;
  // pointsLost?: number;
}

@Component({
  selector: 'app-category-group-component',
  imports: [CommonModule, CategoryFinalComponent, CategoryClassificationGroup],
  templateUrl: './category-group-component.html',
  styleUrl: './category-group-component.scss'
})
export class CategoryGroupComponent implements OnInit, OnDestroy {
  @Input() group!: CategoryGroup;
  @Input() category!: TournamentCategory | undefined;
  categoryType: string | null = null;
  private players: Player[] = [];
  playersGroup: PlayerGroupExtended[] = [];
  private matchesGroup: Match[] = [];
  matchesByDay: MatchByDay[] = [];

  private openSquashService = inject(OpenSquashService);
  esAdmin$!: Observable<boolean>;

  matches: Match[] = [];
  matchesSubscription!: Subscription;

  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  constructor() {
    this.esAdmin$ = this.openSquashService.esAdmin$;
    this.openSquashService.getPlayers().subscribe(players => this.players = players);

    this.route.paramMap.subscribe(params => {
      this.categoryType = params.get('categoryType');
    });
  }


  ngOnInit() {
    this.loadPlayersInGroup();
    this.loadMatches();
  }

  private loadPlayersInGroup() {
    this.playersGroup = this.group?.players || [];
    this.playersGroup.map(playerGroup => {
      const player = this.players.find(p => p.playerId === playerGroup.playerId);
      if (player) {
        playerGroup.player = player;
      }
    });
    this.playersGroup.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  loadMatches() {
    this.matchesSubscription = this.openSquashService.matches$.subscribe(matches => {
      this.matches = matches;
      let matchesCategory = this.matches.filter(m => m.category === this.categoryType && m.groupName === this.group.name).sort((a, b) => {
        const dateA = a.date instanceof Date ? a.date.getTime() : (a.date as any).toMillis();
        const dateB = b.date instanceof Date ? b.date.getTime() : (b.date as any).toMillis();
        return dateA - dateB;
      });

      this.matchesByDay = [];
      matchesCategory.forEach(match => {
        const mext = { ...match } as MatchExtended;
        mext.player1 = this.players.find(p => p.playerId === match.player1Id);
        mext.player2 = this.players.find(p => p.playerId === match.player2Id);

        const dateObj = match.date;
        const dayKey = this.getDateKey(match.date);
        const ed = this.matchesByDay.find(m => this.getDateKey(m.date) === dayKey);
        if (!ed) {
          const md: MatchByDay = {
            date: dateObj,
            matches: [mext]
          };
          this.matchesByDay.push(md);
        } else {
          ed.matches.push(mext);
        }
      });
    });
  }

  private getDateKey(date: Date | Timestamp | undefined) {
    if (date instanceof Date) {
      // return date.toISOString().slice(0, 10);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Meses son 0-indexados
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return date?.toString();
  }

  addPlayer() {
    this.dialog.open(AddPlayerToGroupComponent, {
      data: {
        categoryType: this.categoryType,
        groupName: this.group.name
      }
    }).afterClosed().subscribe(player => {
      if (player) {
        // this.updateData();
        this.snackBar.open(`Jugador agregado al grupo correctamente`, 'Cerrar', {
          duration: 3000
        });
      }
    });
  }

  createPlayer() {
    this.dialog.open(CreatePlayerComponent).afterClosed().subscribe(player => {
      if (player) {
        this.snackBar.open(`Jugador "${player.playerName}" creado correctamente`, 'Cerrar', {
          duration: 3000
        });
      }
    });
  }

  createMatch() {
    this.dialog.open(CreateMatchComponent, {
      data: {
        categoryType: this.categoryType,
        groupName: this.group.name
      }
    }).afterClosed().subscribe(match => {
      if (match) {
        this.snackBar.open(`Se creo el partido correctamente`, 'Cerrar', {
          duration: 3000
        });
      }
    });
  }

  createMatches() {
    this.dialog.open(CreateMatchesComponent, {
      data: {
        categoryType: this.categoryType,
        groupName: this.group.name,
        playersInGroup: this.playersGroup
      }
    }).afterClosed().subscribe(match => {
      if (match) {
        this.snackBar.open(`Se crearon los partidos correctamente`, 'Cerrar', {
          duration: 3000
        });
      }
    });
  }

  startMatch(match: Match) {
    this.dialog.open(StartMatchComponent, {
      data: {
        match: match
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open(`Se inició el partido correctamente`, 'Cerrar', {
          duration: 3000
        });
      }
    });
  }

  resetMatch(match: Match) {
    match.games = [];
    match.state = MatchState.PROGRAMADO;
    match.court = '';
    match.playerIdWinner = null;

    this.openSquashService.updateMatch(match).subscribe(() => {
      this.snackBar.open(`Se reseteo el marcador del partido`, 'Cerrar', {
        duration: 3000
      });
    });
  }

  deleteMatch(match: Match) {
    this.openSquashService.deleteMatch(match).subscribe(() => {
      this.snackBar.open(`Se eliminó el marcador del partido`, 'Cerrar', {
        duration: 3000
      });
    });
  }

  endMatch(match: Match) {
    this.dialog.open(SetMatchScoreComponent, {
      data: {
        match: match,
        category: this.category,
      }
    }).afterClosed().subscribe(match => {
      if (match) {
        this.loadMatches();
        this.snackBar.open(`Se actualizó el marcador del partido`, 'Cerrar', {
          duration: 3000
        });
      }
    });
  }

  recalculate() {
    console.log('Recalculando todo el grupo');
    console.log(this.category);
    console.log('group', this.group);
    console.log('categoryType', this.categoryType);
    console.log('matches', this.matchesByDay);

    this.group.players = this.playersGroup.map(pg => {
      pg.order = 0;
      pg.matchesWon = 0;
      pg.matchesLost = 0;
      pg.setsWon = 0;
      pg.setsLost = 0;
      pg.pointsWon = 0;
      pg.pointsLost = 0;
      pg.matchesPlayed = 0;
      return pg;
    });

    this.matchesByDay.forEach(mbd => {
      mbd.matches.forEach(match => {
        if (match.state === MatchState.FINALIZADO) {
          const player1 = this.group.players.find(p => p.playerId === match.player1Id);
          const player2 = this.group.players.find(p => p.playerId === match.player2Id);

          if (player1 && player2) {
            player1.matchesPlayed = (player1.matchesPlayed ?? 0) + 1;
            player2.matchesPlayed = (player2.matchesPlayed ?? 0) + 1;

            player1.matchesWon = (player1.matchesWon ?? 0) + (match.playerIdWinner === player1.playerId ? 1 : 0);
            player1.matchesLost = (player1.matchesLost ?? 0) + (match.playerIdWinner === player2.playerId ? 1 : 0);
            player2.matchesWon = (player2.matchesWon ?? 0) + (match.playerIdWinner === player2.playerId ? 1 : 0);
            player2.matchesLost = (player2.matchesLost ?? 0) + (match.playerIdWinner === player1.playerId ? 1 : 0);

            player1.setsWon = (player1.setsWon ?? 0) + (match.player1GamesWon ?? 0);
            player1.setsLost = (player1.setsLost ?? 0) + (match.player1GamesLost ?? 0);
            player2.setsWon = (player2.setsWon ?? 0) + (match.player2GamesWon ?? 0);
            player2.setsLost = (player2.setsLost ?? 0) + (match.player2GamesLost ?? 0);

            player1.pointsWon = (player1.pointsWon ?? 0) + (match.player1PointsWon ?? 0);
            player1.pointsLost = (player1.pointsLost ?? 0) + (match.player1PointsLost ?? 0);
            player2.pointsWon = (player2.pointsWon ?? 0) + (match.player2PointsWon ?? 0);
            player2.pointsLost = (player2.pointsLost ?? 0) + (match.player2PointsLost ?? 0);
          }
        }
      });
    });

    const sortedPlayers = [...this.group.players].sort((a, b) => {
      // Ordenar por matchesWon descendente
      const matchesDiff = (b.matchesWon || 0) - (a.matchesWon || 0);
      if (matchesDiff !== 0) return matchesDiff;

      // Si empate, ordenar por diferencia setsWon - setsLost descendente
      const setsDiffA = (a.setsWon || 0) - (a.setsLost || 0);
      const setsDiffB = (b.setsWon || 0) - (b.setsLost || 0);
      const setsDiff = setsDiffB - setsDiffA;
      if (setsDiff !== 0) return setsDiff;

      // Si empate, ordenar por diferencia pointsWon - pointsLost descendente
      const pointsDiffA = (a.pointsWon || 0) - (a.pointsLost || 0);
      const pointsDiffB = (b.pointsWon || 0) - (b.pointsLost || 0);
      return pointsDiffB - pointsDiffA;
    });

    // Asignar el orden comenzando en 1
    sortedPlayers.forEach((player, idx) => {
      player.order = idx + 1;
    });

    this.openSquashService.updateCategory(this.category!).subscribe();

    console.log('Jugadores ordenados', sortedPlayers);
    console.log('Jugadores en group', this.category);

  }

  ngOnDestroy(): void {
    this.matchesSubscription.unsubscribe();
  }

}
